import { useEffect, useState } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { routes } from "./routes";
import { useGetToken } from "./hooks/token";
import Login from "./pages/Login";
import { useDispatch, useSelector } from "react-redux";
import { useAuth, useUser } from "@clerk/clerk-react";
import {
  setCurrentUser,
  setMovers,
  setSelectedMover,
} from "./store/mover/slice";
import {
  useGetMeQuery,
  moverApi,
  useUpdateMoverMutation,
} from "./store/mover/apis";
import { setAuthToken, setGetTokenFn } from "./store/session/slice";
import ErrorBoundary from "./components/ErrorBoundary";
import ErrorFallback from "./components/ErrorFallback";
import RootLayout from "./layouts/root-layout";
import AppLayout from "./layouts/app-layout";
import { Mover } from "./types";
import { RootState } from "./store";
import BillOfLading from "./pages/Moves/components/BillOfLading";
import CustomerPaymentLinks from "./pages/CustomerPaymentLinks";
import {
  initializeBrowserPushNotifications,
  initializeAppPushNotifications,
  requestNotificationPermission,
} from "./utils/pushNotifications";
import CustomerPortal from "./pages/CustomerPortal";
import AnonLayout from "./layouts/anon-layout";
import Estimate from "./pages/Moves/components/Estimate";
import { mobileRoutes } from "./routes/mobile";
import OnboardingGuard from "./components/OnboardingGuard";
import ThumbtackOauth from "./pages/Integrations/ThumbtackOauth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";
import ManageInventory from "./pages/CustomerPortal/ManageInventory";

const App = () => {
  const dispatch = useDispatch();
  const [isAppReady, setIsAppReady] = useState(false);
  const { isSignedIn, isLoaded } = useUser();
  const { getToken } = useAuth();
  const getTokenFn = useGetToken();
  const [clerkToken, setClerkToken] = useState<string | null>(null);
  const { mobileToken } = useSelector((state: RootState) => state.session);
  const urlParams = new URLSearchParams(window.location.search);
  const isWebView = urlParams.get("wv") === "1";
  const [mover, setMover] = useState<Mover | null>(null);
  const [updateMover] = useUpdateMoverMutation();

  // fetch user's default mover data when authenticated
  // auth can be via clerk or a token provided by the mobile app
  // const { data: movers, isError: isMoverError } = useGetUserMoverQuery(
  //   undefined,
  //   { skip: !(clerkToken || mobileToken) }
  // );

  const { data: me, isError } = useGetMeQuery(undefined, {
    skip: !(clerkToken || mobileToken),
  });

  useEffect(() => {
    // we store Clerk's getTokenFn in Redux
    // because we need to fetch a new token at the time of
    // making an API call since the tokens are short lived (1 min)
    // see baseApi.ts for more details
    dispatch(setGetTokenFn(getTokenFn));
  }, [dispatch, getTokenFn]);

  useEffect(() => {
    const fetchClerkToken = async () => {
      const token = await getToken();
      if (token) {
        dispatch(setAuthToken(token));
        setClerkToken(token);
      }
    };

    if (!isLoaded) return;

    if (!isSignedIn) {
      setIsAppReady(true);
    } else {
      fetchClerkToken();
    }
  }, [dispatch, mobileToken, isSignedIn, isLoaded, getToken]);

  useEffect(() => {
    if (me?.user && me?.movers?.length > 0) {
      const selectedMoverId = localStorage.getItem("selectedMoverId");
      const moverId = parseInt(selectedMoverId || me?.movers[0].id.toString());
      const resultAction = moverApi.endpoints.getMover.initiate({
        id: moverId,
      });
      const promise = dispatch(resultAction as any);
      promise.unwrap().then((mover: Mover) => {
        dispatch(setMovers(me.movers));
        dispatch(setSelectedMover(mover));
        setMover(mover);
        dispatch(setCurrentUser(me.user));
        initializeAppPushNotifications(me?.user?.id || 0).catch((error) => {
          console.error("Failed to initialize push notifications:", error);
        });
      });
    }
  }, [dispatch, me]);

  useEffect(() => {
    if (!mover) return;

    if (
      mover?.mover_setup?.free_trial_ends_on &&
      !mover?.mover_setup?.free_trial_tos_accepted_on &&
      !mover?.mover_setup?.free_trial_graduated
    ) {
      setShowTrialTerms(true);
    } else {
      console.log("Initializing Beams Registration");
      requestNotificationPermission().then(() => {
        initializeBrowserPushNotifications(me?.user?.id || 0).catch((error) => {
          console.error("Failed to initialize push notifications:", error);
        });
      });
      setIsAppReady(true);
    }
  }, [mover]);

  const [showTrialTerms, setShowTrialTerms] = useState(false);

  const handleAcceptTerms = async () => {
    try {
      await updateMover({
        data: {
          mover_setup_attributes: {
            id: mover?.mover_setup?.id,
            free_trial_tos_accepted_on: new Date().toISOString(),
            free_trial_tos_accepted_by: me?.user?.id,
          },
        },
      }).unwrap();
      setShowTrialTerms(false);
      console.log("Initializing Beams Registration");
      requestNotificationPermission().then(() => {
        initializeBrowserPushNotifications(me?.user?.id || 0).catch((error) => {
          console.error("Failed to initialize push notifications:", error);
        });
      });
      setIsAppReady(true);
    } catch (error) {
      console.error("Error accepting terms:", error);
    }
  };

  const router = createBrowserRouter([
    {
      element: <AnonLayout />,
      children: [
        { path: "/login", element: <Login /> },
        { path: "/estimate/:ref", element: <Estimate /> },
        { path: "/bol/:ref", element: <BillOfLading /> },
        { path: "/pay/:digest", element: <CustomerPaymentLinks /> },
        // legacy customer hub paths
        { path: "/hub/move/:digest", element: <CustomerPortal /> },
        { path: "/hub/move/:digest/inventory", element: <ManageInventory /> },
        // new customer hub paths
        { path: "/hub/:digest", element: <CustomerPortal /> },
        { path: "/hub/:digest/inventory", element: <ManageInventory /> },
        {
          path: "/integrations/thumbtack/oauth",
          element: <ThumbtackOauth />,
        },
      ],
    },
    {
      element: <RootLayout />,
      children: [
        {
          element: <AppLayout />,
          path: "/m/",
          children: mobileRoutes,
        },
        {
          element: (
            <OnboardingGuard>
              <AppLayout />
            </OnboardingGuard>
          ),
          path: "/",
          children: routes,
        },
      ],
    },
  ]);

  if (isWebView && !mobileToken) return null;
  if (!isAppReady && showTrialTerms)
    return (
      <Dialog open={showTrialTerms} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-2xl"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold prose text-yellow-600">
              Welcome to Software For Movers!
            </DialogTitle>
            <DialogDescription className="text-base text-stone-800">
              <p className="mb-4">Hi {me?.user?.first_name},</p>
              <p className="mb-4">
                Welcome to our VIP Early Access Trial! You have access to every
                feature (except payment processing if you choose to use ours)
                until{" "}
                {format(
                  parseISO(mover?.mover_setup?.free_trial_ends_on || ""),
                  "MMMM d, yyyy"
                )}
                .
              </p>
              <p className="mb-4">
                In good faith, we ask the following of you:
              </p>
              <ol className="list-decimal pl-6 mb-4 space-y-2">
                <li>
                  You agree to actively use the software for leads, estimates,
                  and jobs and give feedback to help us improve the product.
                </li>
                <li>
                  At the end of this period, if you decide not to switch to a
                  paid plan, you agree to provide us a case study of what worked
                  well and what didn't for you.
                </li>
                <li>
                  You understand that this is a confidential trial and agree to
                  not share any information about this trial and/or our
                  software, with any third party providing a product or service
                  similar to Software For Movers, now or in the future.
                </li>
              </ol>
              <p className="mb-4">
                We're excited for you to take our product for a spin and
                hopefully join us for the long term!
              </p>
              <p className="mb-4">
                Vineet
                <br />
                Creator and CEO, Software For Movers
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <p className="text-sm text-gray-700 mr-2">
              By accepting and continuing {mover?.name} agrees to the conditions
              above and also agrees to be bound by our general terms of use for
              the duration of the trial.
            </p>
            <Button
              size="lg"
              variant="default"
              onClick={handleAcceptTerms}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white border-none"
            >
              I accept, continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  if (isError) {
    return <div>Error loading data. Please contact support.</div>;
  }

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
};

export default App;
